import React, { useState } from 'react';
import type { Page, NavParams } from '@types';
import { WorkflowGuidancePanel } from './WorkflowGuidancePanel';

interface PillarsDashboardProps {
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
    purpose?: 'consultant' | 'agency' | 'corporate';
    clientName?: string;
}

export const PillarsDashboard: React.FC<PillarsDashboardProps> = ({ onNavigate, purpose, clientName }) => {
    
    const [isGuideOpen, setIsGuideOpen] = useState(false);
    // Default context text based on purpose
    const getContextBanner = () => {
        if (purpose === 'consultant') return 'Operating Workspace: Independent Consultant';
        if (purpose === 'corporate') return 'Operating Workspace: Corporate Program Manager';
        if (purpose === 'agency' && clientName) return `Managing Client Workspace: ${clientName}`;
        if (purpose === 'agency') return 'Managing Client Workspace';
        return 'IntelOptics Insights Workspace';
    };

    const pillars = [
        {
            name: 'GeoPolitical Risk Management and Intelligence',
            description: 'Access global monitoring, threat group tracking, and intelligence reporting tools.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
            ),
            color: 'bg-yellow-50 text-yellow-700',
            borderColor: 'border-yellow-200 hover:border-yellow-400'
        },
        {
            name: 'Security Risk Management',
            description: 'Conduct threat assessments, generate security plans, and manage facility and executive risk.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.745 3.745 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
                </svg>
            ),
            color: 'bg-blue-50 text-blue-700',
            borderColor: 'border-blue-200 hover:border-blue-400'
        },
        {
            name: 'Business Resiliency and Crises management',
            description: 'Tools for strategic crisis response, emergency preparedness, health and safety, and compliance.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m7.93-9.043A9 9 0 1 1 12 3a9 9 0 0 1 7.93 6.043Z" />
                </svg>
            ),
            color: 'bg-green-50 text-green-700',
            borderColor: 'border-green-200 hover:border-green-400'
        },
        {
            name: 'Learning Management Suite (LMS)',
            description: 'Access the learning portal for team competencies, certifications, and security standards.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
                </svg>
            ),
            color: 'bg-cyan-50 text-cyan-700',
            borderColor: 'border-cyan-200 hover:border-cyan-400'
        }
    ];

    return (
        <div className="animate-fade-in max-w-6xl mx-auto space-y-8">
            {/* Context Banner */}
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 flex items-center shadow-lg animate-fade-in-down">
                <div className="bg-slate-800 p-2 rounded-lg mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-green-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Zm-3 6h.008v.008h-.008v-.008Zm-3 6h.008v.008h-.008v-.008Z" />
                    </svg>
                </div>
                <div>
                    <h2 className="text-white font-bold text-lg">{getContextBanner()}</h2>
                    <p className="text-slate-400 text-sm">All intelligence generated will be automatically saved to this workspace.</p>
                </div>
                <div className="ml-auto flex items-center space-x-3">
                    <button
                        onClick={() => setIsGuideOpen(true)}
                        className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 px-4 py-2 rounded-lg text-sm font-bold transition-colors border border-blue-500/30 flex items-center shadow-inner"
                    >
                        <span className="mr-2">💡</span> Workflow Guide
                    </button>
                    <button
                        onClick={() => onNavigate({ page: 'knowledgeHub' })}
                        className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-lg text-sm transition-colors border border-slate-600"
                    >
                        Change Workspace
                    </button>
                </div>
            </div>

            {/* Global Command Center Prominent CTA */}
            <div 
                onClick={() => onNavigate({ page: 'organizationDashboard' })}
                className="mt-8 bg-gradient-to-r from-[#0B1121] to-slate-900 border border-blue-500/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(59,130,246,0.15)] flex flex-col md:flex-row items-center justify-between cursor-pointer group hover:border-blue-500/60 transition-all duration-500 transform hover:-translate-y-1 relative overflow-hidden"
            >
                <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center relative z-10">
                    <div className="bg-blue-600/20 p-4 rounded-xl border border-blue-500/30 text-blue-400 mr-6 shadow-inner">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-white uppercase tracking-tight group-hover:text-blue-400 transition-colors">Enterprise Control Center</h2>
                        <p className="text-slate-400 mt-2 font-medium">Access unified oversight: Real-time intelligence, active operations, and enterprise metrics in a single pane of glass.</p>
                    </div>
                </div>
                <div className="mt-6 md:mt-0 relative z-10 flex-shrink-0">
                    <button className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-lg shadow-lg shadow-blue-500/30 group-hover:shadow-blue-500/50 transition-all uppercase tracking-widest text-sm flex items-center">
                        Initialize
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 ml-2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="text-center py-10 mt-4 border-t border-slate-200">
                <h1 className="text-3xl font-bold text-slate-800 mb-3">Modular Intelligence Pillars</h1>
                <p className="text-slate-500 max-w-2xl mx-auto">
                    Select a core pillar below to access its deep-dive tactical suites and analysis workflows.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {pillars.map((pillar) => (
                    <div 
                        key={pillar.name}
                        onClick={() => {
                            if (pillar.name === 'Learning Management Suite (LMS)') {
                                onNavigate({ page: 'suiteDashboard', params: { suiteName: pillar.name }});
                            } else {
                                onNavigate({ page: 'dashboard', params: { industry: pillar.name }});
                            }
                        }}
                        className={`bg-white rounded-2xl p-8 border hover:shadow-xl transition-all duration-300 cursor-pointer group flex flex-col ${pillar.borderColor}`}
                    >
                        <div className={`p-4 rounded-xl inline-flex self-start mb-6 ${pillar.color}`}>
                            {pillar.icon}
                        </div>
                        
                        <h3 className="text-2xl font-bold text-slate-800 mb-3 group-hover:text-blue-600 transition-colors">
                            {pillar.name}
                        </h3>
                        
                        <p className="text-slate-600 mb-8 flex-grow">
                            {pillar.description}
                        </p>
                        
                        <div className="flex items-center text-blue-600 font-semibold opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                            Access Pillar Tools
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 ml-2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="mt-12 text-center text-slate-500 text-sm">
                Return to the <button onClick={() => onNavigate({ page: 'knowledgeHub' })} className="text-blue-600 hover:underline">main menu</button> to read the instruction manual.
            </div>
            
            <WorkflowGuidancePanel 
                isOpen={isGuideOpen} 
                onClose={() => setIsGuideOpen(false)} 
                moduleKey="pillars" 
            />
        </div>
    );
};
